import React from "react";
import axios from "axios";

import {
    Button,
    ButtonGroup,
    Chip,
    IconButton,
    Sheet,
    Stack,
    Textarea,
    Typography,
} from "@mui/joy";

import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import SendRoundedIcon from "@mui/icons-material/SendRounded";

export type FeedbackProps = {
    messageId: string;
};

enum FeedbackState {
    NotVisible,
    ShowRatingButtons,
    ShowFeedbackForm,
    ShowThankYouMessage,
}

export default function Feedback(props: FeedbackProps) {
    const messageId = props.messageId;

    const [feedbackState, setFeedbackState] = React.useState<FeedbackState>(
        FeedbackState.NotVisible,
    );
    const [selectedResponseRating, setSelectedResponseRating] = React.useState("none");
    const [feedbackTextAreaValue, setFeedbackTextAreaValue] = React.useState("");

    // Hide Thank You feedback message if one is present.
    React.useEffect(() => {
        if (feedbackState === FeedbackState.ShowThankYouMessage) {
            const timeout = setTimeout(() => {
                setFeedbackState(FeedbackState.NotVisible);
            }, 3000);
            return () => {
                clearTimeout(timeout);
            };
        }
    }, [feedbackState]);

    const submitFeedback = async () => {
        const feedbackValue = feedbackTextAreaValue.trim();

        setFeedbackTextAreaValue("");
        setSelectedResponseRating("none");
        setFeedbackState(FeedbackState.ShowThankYouMessage);

        await axios.post(`/api/message/${messageId}/feedback`, {
            quality: selectedResponseRating,
            feedback: feedbackValue,
        });
    };

    return (
        <>
            {feedbackState === FeedbackState.NotVisible && (
                <Button
                    color="neutral"
                    variant="plain"
                    size="sm"
                    sx={{ textAlign: "left", paddingLeft: 0, alignSelf: "flex-start" }}
                    onMouseEnter={() => setFeedbackState(FeedbackState.ShowRatingButtons)}
                >
                    <Typography level="title-sm" color="neutral">
                        Rate response
                    </Typography>
                </Button>
            )}

            {feedbackState === FeedbackState.ShowRatingButtons && (
                <ButtonGroup
                    sx={{ "--ButtonGroup-separatorColor": "none !important" }}
                    onMouseLeave={() => setFeedbackState(FeedbackState.NotVisible)}
                >
                    <Button
                        color="danger"
                        size="sm"
                        variant="plain"
                        onClick={() => {
                            setSelectedResponseRating("not-good");
                            setFeedbackState(FeedbackState.ShowFeedbackForm);
                        }}
                    >
                        Not good
                    </Button>
                    <Button
                        color="neutral"
                        size="sm"
                        variant="plain"
                        onClick={() => {
                            setSelectedResponseRating("okay");
                            setFeedbackState(FeedbackState.ShowFeedbackForm);
                        }}
                    >
                        Okay
                    </Button>
                    <Button
                        color="success"
                        size="sm"
                        variant="plain"
                        onClick={() => {
                            setSelectedResponseRating("great");
                            setFeedbackState(FeedbackState.ShowFeedbackForm);
                        }}
                    >
                        Great!
                    </Button>
                </ButtonGroup>
            )}

            {feedbackState === FeedbackState.ShowFeedbackForm && (
                <Sheet
                    variant="outlined"
                    color="primary"
                    sx={(theme) => ({
                        backgroundColor: "white",
                        borderRadius: theme.radius.md,
                    })}
                >
                    <Stack direction="column" spacing={1} padding={2}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography level="title-md" color="primary">
                                Tell us more ...
                            </Typography>

                            <IconButton
                                aria-label="Close"
                                size="sm"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setFeedbackState(FeedbackState.NotVisible);
                                }}
                            >
                                <CloseOutlinedIcon />
                            </IconButton>
                        </Stack>

                        <Typography level="body-sm">Response rating</Typography>
                        <ButtonGroup>
                            <Button
                                color="danger"
                                size="sm"
                                variant={
                                    selectedResponseRating === "not-good" ? "solid" : "outlined"
                                }
                                onClick={() => setSelectedResponseRating("not-good")}
                            >
                                Not good
                            </Button>
                            <Button
                                color="neutral"
                                size="sm"
                                variant={selectedResponseRating === "okay" ? "solid" : "outlined"}
                                onClick={() => setSelectedResponseRating("okay")}
                            >
                                Okay
                            </Button>
                            <Button
                                color="success"
                                size="sm"
                                variant={selectedResponseRating === "great" ? "solid" : "outlined"}
                                onClick={() => setSelectedResponseRating("great")}
                            >
                                Great!
                            </Button>
                        </ButtonGroup>

                        <Stack></Stack>

                        <Textarea
                            value={feedbackTextAreaValue}
                            onChange={(e) => setFeedbackTextAreaValue(e.target.value)}
                            minRows={3}
                            placeholder="What were you expecting and what did you get?"
                        />
                        <Button
                            size="sm"
                            color="primary"
                            sx={{ alignSelf: "flex-end", borderRadius: "sm" }}
                            endDecorator={<SendRoundedIcon />}
                            onClick={submitFeedback}
                        >
                            Submit
                        </Button>
                    </Stack>
                </Sheet>
            )}

            {feedbackState === FeedbackState.ShowThankYouMessage && (
                <Chip color="success" variant="soft">
                    Thank you! We will be reviewing your feedback shortly.
                </Chip>
            )}
        </>
    );
}
